import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { selectFilters } from '../../reducers/filtersReducer';
import Header from '../Header/Header';
import useFilterOptions from '../../hooks/useFilterOptions';
import { selectZipCode } from '../../reducers/zipCodeReducer';
import FeaturedOffersList from '../FeaturedOffers/FeaturedOffersList';
import { device } from '../../styles/devices';
import OffersListContainer from '../OffersListContainer';
import { groupOffersByModelGroup, groupModelFamily } from '../../utils/groupOffers';
import { sortByDag, sortVehicleByModel } from '../../utils/sortVehiclesByModel';
import { SORTING_CONFIG } from '../../constants/ModelFamilyList';
import getRandomFeaturedOffers from '../../utils/getRandomFeaturedOffers';
import sortFeaturedOffers from '../../utils/sortFeaturedOffers';
import {
  FeaturedOfferSection,
  OffersListSection,
  OffersListWrapper,
  GeneralSidePadding,
  SkipToSection,
} from './OffersList.styles';
import FilterTags from '../FilterResults/FilterTags';
import ResultsCount from '../ResultsCount';
import { selectOffers } from '../../reducers/offersReducer';
import NoOffers from '../NoOffers';
import { USOffersWrapper } from '../Offers.styles';
import FilterDrawer from '../Filters/FilterDrawer';
import useScrollTracker, {
  useMainComponentScrollTracker,
  useScrollVerticalTracker,
} from '../../hooks/useScrollTracker';
import ShowFilterCTA from '../ShowFilterCTA';
import SkeletonLoader from '../Skeleton';
import NoFilteredOffers from '../NoOffers/NoFilteredOffers';
import SkipTo from '../SkipTo';

type MainPageProps = {
  loading: boolean;
};

const OffersList: React.FC<MainPageProps> = ({ loading }) => {
  const isLaptop = useMediaQuery({ query: device.laptop });
  const isStickyLaptop = useMediaQuery({ query: device.laptop });
  const isBigMobile = useMediaQuery({ query: '(min-width: 480px)' });
  const isResolutionForSticky = isBigMobile && !isStickyLaptop;

  const contentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const listRef = useRef(null);

  const { confirmedZipcode, market } = useSelector(selectZipCode);
  const { confirmedFilters } = useSelector(selectFilters);
  const { offersList } = useSelector(selectOffers);

  const isCaMarket = market === 'CA';

  const [isFiltersOpen, setIsFiltersOpen] = useState(!!isLaptop);

  const isNatOffers = confirmedZipcode === '';

  // For scrolling and animation
  // ----------------------------------------------------------------------------
  const distanceFromTopToMainComponent = useMainComponentScrollTracker(listRef);
  const isReachedEndOfContainer = useScrollVerticalTracker(listRef);
  const mobileScrolled = useScrollTracker(368);
  const scrolled = useScrollTracker(distanceFromTopToMainComponent - 28);
  // ----------------------------------------------------------------------------

  // Filtering and splitting offers logic
  // ----------------------------------------------------------------------------
  const { modelFamilyOptions } = useFilterOptions();

  const groupedModelFamilies = useMemo(() => {
    return groupModelFamily(modelFamilyOptions);
  }, [modelFamilyOptions]);

  const excludeKeys = ['vehicleType', 'offerRegion'];

  const getSelectedFiltersCount = Object.entries(confirmedFilters)
    .filter(([key]) => !excludeKeys.includes(key))
    .filter(([key, value]) => (Array.isArray(value) ? value.length > 0 : value)).length;

  const isAnyFilterSelected = useMemo(() => {
    return getSelectedFiltersCount > 0;
  }, [getSelectedFiltersCount]);

  const isMultiDagsSelected =
    confirmedFilters.offerRegion.filter((toCheck) => toCheck !== 'NAT').length > 1;

  const filteredOffers = useMemo(() => {
    return offersList.filter((offer) => {
      return (
        (!isCaMarket || confirmedFilters.vehicleType === offer.offerModelDetail.vehicleType) &&
        (!isCaMarket ||
          !confirmedFilters.offerRegion.length ||
          confirmedFilters.offerRegion.includes(offer?.offerModelDetail?.dag)) &&
        (!confirmedFilters.offerType.length ||
          confirmedFilters.offerType.includes(offer.offerType.toLowerCase())) &&
        (!confirmedFilters.bodyType.length ||
          confirmedFilters.bodyType.includes(offer?.offerModelDetail?.bodyType.toLowerCase())) &&
        (!confirmedFilters.modelFamily.length ||
          confirmedFilters.modelFamily.includes(
            offer.offerModelDetail.modelFamily.replace(/[ -]/g, '').toLowerCase(),
          )) &&
        (!confirmedFilters.trimLine.length ||
          confirmedFilters.trimLine.includes(
            offer?.offerModelDetail?.trimline.toLowerCase().replace(' ', '_'),
          )) &&
        (!confirmedFilters.modelYear.length ||
          confirmedFilters.modelYear.includes(offer?.offerModelDetail?.modelYear.toString()))
      );
    });
  }, [
    offersList,
    isCaMarket,
    confirmedFilters.vehicleType,
    confirmedFilters.offerRegion,
    confirmedFilters.offerType,
    confirmedFilters.bodyType,
    confirmedFilters.modelFamily,
    confirmedFilters.trimLine,
    confirmedFilters.modelYear,
  ]);

  const sortedOffers = useMemo(() => {
    const sortedByDagOffers = isCaMarket
      ? sortByDag(filteredOffers, confirmedFilters.offerRegion)
      : filteredOffers;
    const groupedOffers = groupOffersByModelGroup(sortedByDagOffers, groupedModelFamilies);
    const sortedModels = sortVehicleByModel(groupedOffers, SORTING_CONFIG);
    return sortedModels;
  }, [confirmedFilters.offerRegion, filteredOffers, groupedModelFamilies, isCaMarket]);

  const featuredOffers = useMemo(() => {
    if (isAnyFilterSelected || isMultiDagsSelected) return [];

    const sortedFeaturedOffers = sortFeaturedOffers(offersList);
    const featuredOffersList = sortedFeaturedOffers.filter((offer) => offer.programPriority === 1);

    // Filter for CA market to filter by DAG

    // const filteredByDagOffers = IS_CA_MARKET
    //   ? featuredOffersList.filter((toCheck) => {
    //       // Show featured offers only from first DAG in the list
    //       const firstSelectedDag = confirmedFilters.offerRegion[0];
    //       return firstSelectedDag.includes(toCheck?.offerModelDetail?.dag);
    //     })

    const firstSelectedDag = confirmedFilters.offerRegion[0];

    const filteredByDagOffers = isCaMarket
      ? featuredOffersList.filter((toCheck) => toCheck.offerModelDetail.dag === firstSelectedDag)
      : featuredOffersList;

    // Filter for CA market to filter by vehicle Type (no featured for CPO for now)
    const filteredByVehicleType = isCaMarket
      ? filteredByDagOffers.filter(
          (toCheck) => toCheck.offerModelDetail.vehicleType === confirmedFilters.vehicleType,
        )
      : filteredByDagOffers;

    // Filter by offer type. Only Lease offers for CA for MVP
    const filteredByOfferType = isCaMarket
      ? filteredByVehicleType.filter((toCheck) => toCheck.offerType === 'lease')
      : filteredByVehicleType;

    const featuredOffersCount = filteredByOfferType.length;
    if (featuredOffersCount < 4 && !isCaMarket) {
      const randomFeaturedOffers = getRandomFeaturedOffers(sortedFeaturedOffers);
      return randomFeaturedOffers;
    }
    return filteredByOfferType?.slice(0, 5);
  }, [
    confirmedFilters.offerRegion,
    confirmedFilters.vehicleType,
    isAnyFilterSelected,
    isCaMarket,
    isMultiDagsSelected,
    offersList,
  ]);

  // ----------------------------------------------------------------------------

  // Code realted to smooth scrolling in Skip To section
  // ----------------------------------------------------------------------------

  const handleOpenFilters = useCallback(() => {
    setIsFiltersOpen(true);
  }, []);

  const setRef = (key: string) => (el: HTMLDivElement | null) => {
    contentRefs.current[key] = el;
  };
  // ----------------------------------------------------------------------------

  const RenderShowFiltersSticky = useMemo(() => {
    // Complex logic to show Show Filters
    // Case 1: laptop and filters are not open
    // Case 2, less than laptop (1024px), but more than 480px, scrolled till regular show filter dissapear
    // and filter are not opened
    if (
      (!isFiltersOpen && isStickyLaptop) ||
      (mobileScrolled && !isFiltersOpen && isResolutionForSticky)
    )
      return (
        <ShowFilterCTA
          isFiltersOpen={isFiltersOpen}
          setIsFiltersOpen={setIsFiltersOpen}
          scrolled={scrolled}
        />
      );
    return null;
  }, [isFiltersOpen, isStickyLaptop, mobileScrolled, scrolled, isResolutionForSticky]);

  const shouldRenderNoFilteredOffers =
    isCaMarket && offersList.length && !loading && !filteredOffers.length;

  const RenderOffers = useMemo(() => {
    if ((!offersList && loading) || loading) return <SkeletonLoader />;
    if ((!offersList && !loading) || !offersList?.length) return <NoOffers />;
    return (
      <OffersListWrapper>
        <GeneralSidePadding isFiltersOpen={isFiltersOpen}>
          <Header
            isNational={isNatOffers}
            dag={filteredOffers[0]?.zipClusterID}
            isFiltersOpen={isFiltersOpen}
          />
        </GeneralSidePadding>
        <GeneralSidePadding isFiltersOpen={isFiltersOpen}>
          <SkipToSection data-cy="skip-to-section">
            <SkipTo
              handleOpenFilters={handleOpenFilters}
              isFiltersOpen={isFiltersOpen}
              selectedFiltersCount={getSelectedFiltersCount}
            />
          </SkipToSection>
        </GeneralSidePadding>
        <GeneralSidePadding isFiltersOpen={isFiltersOpen}>
          <FilterTags />
        </GeneralSidePadding>
        {isAnyFilterSelected && (
          <GeneralSidePadding isFiltersOpen={isFiltersOpen}>
            <ResultsCount totalCount={filteredOffers.length} />
          </GeneralSidePadding>
        )}
        <FeaturedOfferSection>
          <FeaturedOffersList featuredOffers={featuredOffers} />
        </FeaturedOfferSection>
        <GeneralSidePadding isFiltersOpen={isFiltersOpen}>
          <OffersListSection>
            <OffersListContainer
              handleSectionRef={setRef}
              offers={sortedOffers}
              groupedModels={groupedModelFamilies}
              isAnyFilterSelected={isAnyFilterSelected}
              modelFamilyOptions={modelFamilyOptions}
              isFiltersOpen={isFiltersOpen}
            />
          </OffersListSection>
          {shouldRenderNoFilteredOffers && <NoFilteredOffers />}
        </GeneralSidePadding>
      </OffersListWrapper>
    );
  }, [
    featuredOffers,
    filteredOffers,
    getSelectedFiltersCount,
    groupedModelFamilies,
    handleOpenFilters,
    isAnyFilterSelected,
    isFiltersOpen,
    isNatOffers,
    loading,
    modelFamilyOptions,
    offersList,
    shouldRenderNoFilteredOffers,
    sortedOffers,
  ]);

  return (
    <USOffersWrapper ref={listRef}>
      <FilterDrawer
        isFiltersOpen={isFiltersOpen}
        setIsFiltersOpen={setIsFiltersOpen}
        isReachedEndOfContainer={isReachedEndOfContainer}
      />
      {RenderShowFiltersSticky}
      {RenderOffers}
    </USOffersWrapper>
  );
};

export default OffersList;
